import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { ISSUES, emojiFor } from "@/data/issues";
import { US_STATES } from "@/data/states";
import VoiceCard, { buildMessage } from "@/components/VoiceCard";
import SiteFooter from "@/components/SiteFooter";
import WelcomeScreen from "@/components/campaign/WelcomeScreen";
import LearnScreen from "@/components/campaign/LearnScreen";
import CardModal from "@/components/campaign/CardModal";
import "@/voteforme.css";


const ORDER = ["welcome", "issues", "learn", "pledge", "cta"];
const PROGRESS = { welcome: 0, issues: 22, learn: 52, pledge: 80, cta: 100 };

function slugify(t) {
  return (t || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function Campaign() {
  const [screen, setScreen] = useState("welcome");
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [custom, setCustom] = useState("");
  const [city, setCity] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [email, setEmail] = useState("");
  const hiddenCardRef = useRef(null);
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugStatus, setSlugStatus] = useState(null); // 'ok' | 'taken' | null
  const [gps, setGps] = useState(null);
  const [savedSlug, setSavedSlug] = useState(null);
  const cardTokenRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [cardUrl, setCardUrl] = useState(null);
  const [dlBusy, setDlBusy] = useState(false);
  const cardRef = useRef(null);

  // capture GPS location (optional, prompts permission)
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => console.info("Location not shared:", err.message),
      { timeout: 8000 }
    );
  }, []);

  // default slug from name
  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name));
  }, [name, slugTouched]);

  const championUrl = savedSlug ? `${window.location.origin}/p/${savedSlug}` : "";

  const goTo = (id) => {
    setScreen(id);
    window.scrollTo(0, 0);
    if (id === "cta") {
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 }, colors: ["#FDF71E", "#E02189", "#67DAD7", "#F8D4DE", "#AD1C5D"] });
    }
  };

  const goBack = () => {
    const i = ORDER.indexOf(screen);
    if (i > 0) goTo(ORDER[i - 1]);
  };

  const toggleIssue = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const checkSlug = async () => {
    const s = slugify(slug);
    if (!s) return;
    try {
      const { data } = await api.get(`/pledges/slug-available/${s}`);
      setSlugStatus(data.available ? "ok" : "taken");
    } catch (err) {
      console.warn("Slug check failed", err);
      setSlugStatus(null);
    }
  };

  const uploadCard = async (dataUrl, forSlug) => {
    const blob = await (await fetch(dataUrl)).blob();
    const form = new FormData();
    form.append("file", blob, "voice-card.png");
    const { data } = await api.post(`/pledges/${forSlug}/voice-card`, form, { headers: { Authorization: `Bearer ${cardTokenRef.current}` } });
    setCardUrl(`${process.env.REACT_APP_BACKEND_URL}${data.card_url}`);
  };

  const submitPledge = async () => {
    try {
      const { data } = await api.post("/pledges", {
        name: name.trim(),
        age: age.trim(),
        issues: selected,
        custom_message: custom.trim() || undefined,
        city: city.trim() || undefined,
        state: stateCode || undefined,
        slug: slugify(slug) || undefined,
        gps: gps || undefined,
        email: email.trim() || undefined,
      });
      setSavedSlug(data.slug);
      cardTokenRef.current = data.card_token;
      goTo("cta");
      if (email.trim()) toast.success(`We'll email your card and link to ${email.trim()}`);
      setTimeout(async () => {
        try {
          if (!hiddenCardRef.current) return;
          const canvas = await html2canvas(hiddenCardRef.current, { scale: 3, useCORS: true, allowTaint: true, backgroundColor: "#3A0300", logging: false });
          await uploadCard(canvas.toDataURL("image/png"), data.slug);
        } catch (err) { console.warn("Auto card generation failed; will retry when the card is opened", err); }
      }, 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your pledge. Please try again.");
    }
  };

  const generateAndUpload = async () => {
    const el = hiddenCardRef.current || cardRef.current;
    if (!el) return null;
    const canvas = await html2canvas(el, { scale: 3, useCORS: true, allowTaint: true, backgroundColor: "#3A0300", logging: false });
    const dataUrl = canvas.toDataURL("image/png");
    // upload for permanent link
    if (savedSlug && !cardUrl) {
      try { await uploadCard(dataUrl, savedSlug); } catch (err) { console.warn("Card upload failed", err); toast.error("Your card couldn't be saved online yet — the download still works."); }
    }
    return dataUrl;
  };

  const openModal = async () => {
    setModalOpen(true);
    setTimeout(generateAndUpload, 300);
  };

  const downloadCard = async () => {
    setDlBusy(true);
    try {
      const dataUrl = await generateAndUpload();
      if (dataUrl) {
        const a = document.createElement("a");
        a.download = `VoteForMe_${(name || "voice-card").replace(/\s+/g, "_")}.png`;
        a.href = dataUrl;
        a.click();
        toast.success("Saved to your downloads ✓");
      }
    } catch (err) {
      console.warn("Card download failed", err);
      toast.error("Download failed — try a screenshot!");
    } finally {
      setDlBusy(false);
    }
  };

  const shareText = () => {
    const list = selected.join(", ").toLowerCase();
    const ageStr = age ? `, age ${age},` : "";
    const own = custom.trim() ? `\n\nMy personal message to you: "${custom.trim()}"` : "";
    return `My name is ${name || "A Southern Black girl"}${ageStr} and I can't vote yet, but ${list} matter deeply to me. I'm asking every eligible voter: please vote for me, until I can. My future is on the ballot.${own}\n\n#VoteForMeUntilICan`;
  };

  const copyMessage = () => {
    const text = `${shareText()}${championUrl ? `\n\nMy page: ${championUrl}` : ""}\n\nVoter Toolbox: https://blackvotersmatterfund.org/vote/`;
    navigator.clipboard.writeText(text);
    toast.success("Copied! Paste it anywhere ✓");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(championUrl);
    toast.success("Champion link copied ✓");
  };

  const restart = () => {
    setSelected([]); setName(""); setAge(""); setCustom(""); setCity(""); setStateCode(""); setEmail(""); setSlug(""); setSlugTouched(false);
    setSavedSlug(null); setCardUrl(null); setModalOpen(false);
    goTo("welcome");
  };

  const ctaMessage = () => {
    const list = selected.join(", ").toLowerCase();
    const ageStr = age ? `, age ${age},` : "";
    const own = custom.trim() ? ` ${custom.trim()}` : "";
    return `My name is ${name || "A Southern Black girl"}${ageStr} and I can't vote yet. But ${list} shape my life every single day.${own} I'm asking you — someone who CAN vote — to please vote for me, until I can. Vote like my future depends on it. Because it does. #VoteForMeUntilICan`;
  };

  return (
    <div className="vfm">
      <div className="vfm-bg-glow" />
      <div className="progress-bar"><div className="progress-fill" style={{ width: PROGRESS[screen] + "%" }} /></div>
      {screen !== "welcome" && (
        <button className="back-btn" onClick={goBack} data-testid="back-btn"><ArrowLeft size={15} /> Back</button>
      )}

      {/* WELCOME */}
      <WelcomeScreen active={screen === "welcome"} onStart={() => goTo("issues")} />

      <div className={`screen ${screen === "issues" ? "active" : ""}`} id="screen-issues">
        <div className="screen-inner">
          <div className="screen-header">
            <div className="screen-label">Step 1 of 4</div>
            <h2 className="screen-title">What matters most to you?</h2>
            <p className="screen-desc">Pick up to 3 issues that affect your life and community.</p>
          </div>
          <div className="issues-grid">
            {ISSUES.map((iss) => (
              <div key={iss.id} className={`issue-card ${selected.includes(iss.id) ? "selected" : ""}`} onClick={() => toggleIssue(iss.id)} data-testid={`issue-card-${slugify(iss.id)}`}>
                <span className="issue-emoji">{iss.emoji}</span>
                <div className="issue-name">{iss.name}</div>
              </div>
            ))}
          </div>
          <div className="selection-count"><b data-testid="selection-count">{selected.length}</b> of 3 selected</div>
          <button className="btn-primary" disabled={selected.length === 0} onClick={() => goTo("learn")} style={{ maxWidth: 300, width: "100%" }} data-testid="issues-next-btn">See the Facts →</button>
        </div>
      </div>

      {/* LEARN */}
      <LearnScreen active={screen === "learn"} selected={selected} onNext={() => goTo("pledge")} />

      <div className={`screen ${screen === "pledge" ? "active" : ""}`} id="screen-pledge">
        <div className="screen-inner">
          <div className="screen-header">
            <div className="screen-label">Step 3 of 4</div>
            <h2 className="screen-title">Create your pledge</h2>
            <p className="screen-desc">Enter your name and age, and choose your own personal link.</p>
          </div>
          <div className="input-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label className="input-label">Your First Name</label>
              <input className="text-input" placeholder="e.g. Amara" maxLength={20} value={name} onChange={(e) => setName(e.target.value)} data-testid="pledge-name-input" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label className="input-label">Your Age</label>
              <input className="text-input" type="number" placeholder="14" min={6} max={17} value={age} onChange={(e) => setAge(e.target.value)} data-testid="pledge-age-input" />
            </div>
          </div>
          <div className="input-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label className="input-label">City <span className="lbl-opt">(optional)</span></label>
              <input className="text-input" placeholder="e.g. Birmingham" maxLength={60} value={city} onChange={(e) => setCity(e.target.value)} data-testid="pledge-city-input" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label className="input-label">State <span className="lbl-opt">(optional)</span></label>
              <select className="text-input select-input" value={stateCode} onChange={(e) => setStateCode(e.target.value)} data-testid="pledge-state-select">
                <option value="">—</option>
                {US_STATES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </div>
          </div>
          <div className="slug-field">
            <label className="input-label">Your Personal Link (you can keep it or change it)</label>
            <div className="slug-input-wrap">
              <span className="slug-prefix">{window.location.host}/p/</span>
              <input className="slug-input" value={slug} onChange={(e) => { setSlugTouched(true); setSlug(slugify(e.target.value)); setSlugStatus(null); }} onBlur={checkSlug} placeholder="your-name" data-testid="pledge-slug-input" />
            </div>
            {slugStatus === "ok" && <div className="slug-status ok">✓ This link is available</div>}
            {slugStatus === "taken" && <div className="slug-status taken">Taken — we'll add a number to make it unique</div>}
          </div>
          <div className="input-group" style={{ marginBottom: 18, width: "100%", maxWidth: 480 }}>
            <label className="input-label">My personal message to you <span style={{ opacity: .6, textTransform: "none", letterSpacing: 0 }}>(optional · {160 - custom.length} left)</span></label>
            <textarea className="text-input text-area" placeholder='e.g. "My little brother deserves a safe school and clean water."' maxLength={160} rows={2} value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="pledge-custom-message-input" />
          </div>
          <div className="input-group" style={{ marginBottom: 18, width: "100%", maxWidth: 480 }}>
            <label className="input-label">Email <span className="lbl-opt">(optional · we'll send you your card, link and an edit code if you ever want changes)</span></label>
            <input type="email" className="text-input" placeholder="you@example.com" maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="pledge-email-input" />
          </div>
          <div className="pledge-preview-wrap">
            <VoiceCard name={name} age={age} issues={selected} custom={custom} city={city} state={stateCode} />
          </div>
          <button className="btn-primary" onClick={submitPledge} disabled={!name.trim()} style={{ maxWidth: 480, width: "100%" }} data-testid="pledge-submit-button">Call on Voters →</button>
        </div>
      </div>

      {/* CTA */}
      <div className={`screen ${screen === "cta" ? "active" : ""}`} id="screen-cta">
        <div className="screen-inner">
          <div className="cta-ring">🗳️</div>
          <h2 className="cta-title">Now call on the people who <em>can</em> vote.</h2>
          <div className="cta-msg-box">
            <div className="cta-msg-label">Your message to voters</div>
            <div className="cta-msg-text">{ctaMessage()}</div>
          </div>
          {championUrl && (
            <div style={{ maxWidth: 500, width: "100%", marginBottom: 22 }}>
              <div className="cta-msg-label" style={{ marginBottom: 8 }}>Your personal champion page</div>
              <div className="link-box">
                <code data-testid="champion-url">{championUrl}</code>
                <button className="link-copy-btn" onClick={copyLink} data-testid="copy-champion-link-btn"><Link2 size={13} /></button>
              </div>
            </div>
          )}
          <div className="cta-stats">
            <div className="cta-stat"><span className="cta-stat-num">74M</span><span className="cta-stat-label">Americans under 18 can't vote yet</span></div>
            <div className="cta-stat"><span className="cta-stat-num">60%</span><span className="cta-stat-label">of eligible voters skipped the last midterm</span></div>
            <div className="cta-stat"><span className="cta-stat-num">1 vote</span><span className="cta-stat-label">has decided state races in the South</span></div>
          </div>
          <div className="btn-group">
            <button className="btn-yellow" onClick={openModal} data-testid="get-voice-card-btn"><Sparkles size={17} style={{ marginRight: 6, verticalAlign: "-3px" }} />Get My Voice Card</button>
            <button className="btn-secondary" onClick={restart} data-testid="restart-btn">Start Over</button>
          </div>
        </div>
      </div>

      <SiteFooter />

      {/* hidden card used to auto-generate the hosted image right after submit */}
      <div className="vc-offscreen" aria-hidden="true" ref={hiddenCardRef}>
        <VoiceCard name={name} age={age} issues={selected} custom={custom} city={city} state={stateCode} />
      </div>

      <CardModal
        open={modalOpen} onClose={() => setModalOpen(false)} cardRef={cardRef}
        pledge={{ name, age, issues: selected, custom, city, state: stateCode }}
        shareText={shareText()} championUrl={championUrl} cardUrl={cardUrl} getImage={generateAndUpload}
        onDownload={downloadCard} onCopyMessage={copyMessage} onCopyLink={copyLink} busy={dlBusy}
      />
    </div>
  );
}
